// SuperSystemPage — /super/system.
//
// Four tabs:
//   1. System health — message-queue / cron / platform cards (auto-refresh).
//   2. Audit log — paginated cross-tenant /super/audit-log view with filters.
//   3. Tier Limits — inline-editable module_tier_limits table editor.
//   4. Platform SMTP — platform_smtp_settings editor (cron failure alerts).

import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Badge } from '../../components/ui/Badge/Badge';
import { Tabs } from '../../components/ui/Tabs/Tabs';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import {
  useAllAdmins,
  useAuditLog,
  useSystemHealth,
  type AuditLogEntry,
  type ListAuditLogFilters,
} from './hooks/useSuper';
import { TierLimitsTab } from './TierLimitsTab';
import { PlatformSmtpTab } from './system/tabs/PlatformSmtpTab';
import styles from './SuperSystemPage.module.css';

type ActiveTab = 'health' | 'audit' | 'tier-limits' | 'platform-smtp';

const TAB_ITEMS = [
  { id: 'health', label: 'System health' },
  { id: 'audit', label: 'Audit log' },
  { id: 'tier-limits', label: 'Tier Limits' },
  { id: 'platform-smtp', label: 'Platform SMTP' },
];

const ACTION_OPTIONS = [
  'super_login',
  'tier_change',
  'status_change',
  'impersonate',
  'company_created',
  'admin_created',
  'admin_suspended',
  'password_reset',
] as const;

const PAGE_LIMIT = 50;
const DETAIL_TRIM_AT = 80;

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Not yet run';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function defaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 7);
  return { from: toYmd(from), to: toYmd(now) };
}

function trimDetail(text: string | null): string {
  if (!text) return '';
  if (text.length <= DETAIL_TRIM_AT) return text;
  return `${text.slice(0, DETAIL_TRIM_AT).trimEnd()}…`;
}

export function SuperSystemPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const health = useSystemHealth();
  const [flushOpen, setFlushOpen] = useState(false);
  const [tab, setTab] = useState<ActiveTab>('health');

  // Audit-log local state. Default last-7-day range, action="", admin="".
  const [{ from: defaultFrom, to: defaultTo }] = useState(defaultDateRange);
  const [dateFrom, setDateFrom] = useState<string>(defaultFrom);
  const [dateTo, setDateTo] = useState<string>(defaultTo);
  const [action, setAction] = useState<string>('');
  const [adminId, setAdminId] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const flush = useMutation({
    mutationFn: async () => {
      await apiClient.post(endpoints.notifications.flush);
    },
    onSuccess: () => {
      toast.success('Notification queue flushed.');
      void qc.invalidateQueries({ queryKey: ['super', 'system', 'health'] });
    },
    onError: () => {
      toast.error('Failed to flush queue.');
    },
  });

  const auditFilters: ListAuditLogFilters = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      action: action || undefined,
      admin_id: adminId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [page, action, adminId, dateFrom, dateTo],
  );

  const audit = useAuditLog(auditFilters, { enabled: tab === 'audit' });
  const admins = useAllAdmins({ limit: 100 }, { enabled: tab === 'audit' });

  const data = health.data;

  function onAuditFilterChange(setter: (v: string) => void) {
    return (next: string) => {
      setter(next);
      setPage(1);
      setExpanded(null);
    };
  }

  const auditEntries: AuditLogEntry[] = audit.data?.entries ?? [];
  const auditPagination = audit.data?.pagination;

  const headerTitle =
    tab === 'audit'
      ? 'Audit log'
      : tab === 'tier-limits'
        ? 'Tier Limits'
        : tab === 'platform-smtp'
          ? 'Platform SMTP'
          : 'System health';
  const headerSubtitle =
    tab === 'audit'
      ? 'Cross-tenant admin activity log. Filter by date, action, or admin.'
      : tab === 'tier-limits'
        ? 'Per-module subscription tier limits. Edits apply on the next enforcement check.'
        : tab === 'platform-smtp'
          ? 'SMTP credentials for super-admin cron failure alerts. Separate from per-tenant communication settings.'
          : 'Cross-tenant queue + cron + server status. Auto-refreshes every 30 s.';

  return (
    <div className={styles.page}>
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/super')}
            >
              Back
            </Button>
            {tab === 'health' ? (
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={() => void health.refetch()}
                loading={health.isFetching}
              >
                Refresh
              </Button>
            ) : tab === 'audit' ? (
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={() => void audit.refetch()}
                loading={audit.isFetching}
              >
                Refresh
              </Button>
            ) : null}
          </div>
        }
      />

      <Tabs
        className={styles.tabs}
        items={TAB_ITEMS}
        value={tab}
        onChange={(id) => setTab(id as ActiveTab)}
        ariaLabel="Super system tabs"
      />

      {tab === 'platform-smtp' ? (
        <PlatformSmtpTab />
      ) : tab === 'tier-limits' ? (
        <TierLimitsTab />
      ) : tab === 'health' ? (
        !data ? (
          <Card>
            <div className={styles.empty}>
              <Spinner />
            </div>
          </Card>
        ) : (
          <div className={styles.grid}>
            <Card title="Message queue">
              <ul className={styles.statList}>
                <li>
                  <span>Pending</span>
                  <strong>{formatNumber(data.message_queue.pending)}</strong>
                </li>
                <li>
                  <span>Processing</span>
                  <strong>{formatNumber(data.message_queue.processing)}</strong>
                </li>
                <li>
                  <span>Failed</span>
                  <strong className={data.message_queue.failed > 0 ? styles.failed : ''}>
                    {formatNumber(data.message_queue.failed)}
                  </strong>
                </li>
                <li>
                  <span>Sent (24 h)</span>
                  <strong>{formatNumber(data.message_queue.sent_24h)}</strong>
                </li>
                <li>
                  <span>Skipped (24 h)</span>
                  <strong>{formatNumber(data.message_queue.skipped_24h)}</strong>
                </li>
              </ul>
              <Button
                variant="secondary"
                icon={Trash2}
                onClick={() => setFlushOpen(true)}
                loading={flush.isPending}
              >
                Flush queue
              </Button>
            </Card>

            <Card title="Cron jobs">
              <ul className={styles.statList}>
                <li>
                  <span>Notification dispatcher</span>
                  <strong>{formatRelative(data.crons.notification_last_run)}</strong>
                </li>
                <li>
                  <span>Reminder cron</span>
                  <strong>{formatRelative(data.crons.reminder_last_run)}</strong>
                </li>
                <li>
                  <span>Cleanup cron</span>
                  <strong>{formatRelative(data.crons.cleanup_last_run)}</strong>
                </li>
              </ul>
            </Card>

            <Card title="Platform">
              <ul className={styles.statList}>
                <li>
                  <span>Total companies</span>
                  <strong>{formatNumber(data.companies.total)}</strong>
                </li>
                <li>
                  <span>Active</span>
                  <strong>{formatNumber(data.companies.active)}</strong>
                </li>
                <li>
                  <span>Suspended</span>
                  <strong>{formatNumber(data.companies.suspended)}</strong>
                </li>
                <li>
                  <span>Trial</span>
                  <strong>{formatNumber(data.companies.trial)}</strong>
                </li>
                <li>
                  <span>Server uptime</span>
                  <strong>{formatUptime(data.server.uptime_seconds)}</strong>
                </li>
                <li>
                  <span>Node version</span>
                  <strong className={styles.mono}>{data.server.node_version}</strong>
                </li>
              </ul>
            </Card>
          </div>
        )
      ) : (
        <Card flush>
          <div className={styles.filters}>
            <div className={styles.filterField}>
              <label htmlFor="audit-from">From</label>
              <input
                id="audit-from"
                type="date"
                className={styles.filterInput}
                value={dateFrom}
                onChange={(e) => onAuditFilterChange(setDateFrom)(e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor="audit-to">To</label>
              <input
                id="audit-to"
                type="date"
                className={styles.filterInput}
                value={dateTo}
                onChange={(e) => onAuditFilterChange(setDateTo)(e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor="audit-action">Action</label>
              <select
                id="audit-action"
                className={styles.filterInput}
                value={action}
                onChange={(e) => onAuditFilterChange(setAction)(e.target.value)}
              >
                <option value="">All actions</option>
                {ACTION_OPTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterField}>
              <label htmlFor="audit-admin">Admin</label>
              <select
                id="audit-admin"
                className={styles.filterInput}
                value={adminId}
                onChange={(e) => onAuditFilterChange(setAdminId)(e.target.value)}
              >
                <option value="">All admins</option>
                {(admins.data?.admins ?? []).map((a) => (
                  <option key={a.enc_id} value={a.enc_id}>
                    {a.username}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Company</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {audit.isLoading ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.empty}>
                        <Spinner />
                      </div>
                    </td>
                  </tr>
                ) : auditEntries.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.emptyState}>No audit entries match these filters.</div>
                    </td>
                  </tr>
                ) : (
                  auditEntries.map((entry) => {
                    const isOpen = expanded === entry.id;
                    const hasMoreDetail =
                      (entry.details?.length ?? 0) > DETAIL_TRIM_AT ||
                      entry.old_values !== null ||
                      entry.new_values !== null;
                    return (
                      <Fragment key={entry.id}>
                        <tr
                          className={styles.row}
                          onClick={() =>
                            setExpanded(isOpen ? null : entry.id)
                          }
                        >
                          <td className={styles.timestamp}>
                            {formatTimestamp(entry.created_at)}
                          </td>
                          <td>{entry.admin.username}</td>
                          <td>
                            <Badge variant="info">{entry.action}</Badge>
                          </td>
                          <td>
                            {entry.company ? (
                              entry.company.name
                            ) : (
                              <span className={styles.muted}>—</span>
                            )}
                          </td>
                          <td className={styles.detailCell}>
                            {entry.details ? (
                              <>
                                {trimDetail(entry.details)}
                                {hasMoreDetail ? (
                                  <span className={styles.muted}>
                                    {' '}
                                    {isOpen ? '(less)' : '(more)'}
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <span className={styles.muted}>—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className={styles.expandedRow}>
                            <td
                              colSpan={5}
                              data-testid={`audit-row-expanded-${entry.id}`}
                            >
                              <div className={styles.expandedDetails}>
                                {entry.details ? (
                                  <div>
                                    <h4>Details</h4>
                                    <pre>{entry.details}</pre>
                                  </div>
                                ) : null}
                                {entry.old_values !== null ? (
                                  <div>
                                    <h4>old_values</h4>
                                    <pre>
                                      {JSON.stringify(entry.old_values, null, 2)}
                                    </pre>
                                  </div>
                                ) : null}
                                {entry.new_values !== null ? (
                                  <div>
                                    <h4>new_values</h4>
                                    <pre>
                                      {JSON.stringify(entry.new_values, null, 2)}
                                    </pre>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {auditPagination && auditPagination.totalPages > 1 ? (
            <div className={styles.paginationWrap}>
              <Pagination
                pagination={auditPagination}
                onPageChange={(next) => {
                  setPage(next);
                  setExpanded(null);
                }}
              />
            </div>
          ) : null}
        </Card>
      )}

      <ConfirmModal
        open={flushOpen}
        title="Flush notification queue?"
        confirmLabel="Yes, flush"
        variant="danger"
        loading={flush.isPending}
        onConfirm={async () => {
          await flush.mutateAsync();
          setFlushOpen(false);
        }}
        onCancel={() => setFlushOpen(false)}
        message="This drains pending and failed messages from the queue. Use only when you've confirmed they are no longer needed — drained messages are not re-sent."
      />
    </div>
  );
}
